/**
 * PLAB-06 (WOOPF #79, ported): load the Zendesk widget on demand instead of on every page load.
 *
 * The widget was a parser-blocking `<script id="ze-snippet" src="...">` at the end of `<body>` in
 * layout/theme.liquid. At WOOPF the same tag cost 171 ms of main thread on every page, measured
 * on Slow 4G with 4x CPU throttling, for a widget most visitors never open. Nothing about it is
 * needed before a visitor asks for help, so it now waits for one of:
 *
 *   - the first pointerdown, keydown, touchstart or scroll anywhere on the page;
 *   - a click on any element carrying `data-pz-help` (no element carries it today - it is the
 *     hook for a future Help affordance in the header or footer);
 *   - the browser going idle after `load`, so the launcher still appears for a visitor who only
 *     reads.
 *
 * Whichever fires first wins; the rest are removed, so a long session does not keep a
 * capture-phase handler on every scroll. The snippet URL and its widget key stay in Liquid, read
 * from the carrier tag's `data-pz-zendesk-src`, so nothing about where the key lives changes.
 *
 * NO `integrity=`. Zendesk publishes no Subresource Integrity hash for `snippet.js` and serves
 * content that changes per request, so a hash would make the browser refuse to run it. The tag it
 * replaced never carried one either.
 *
 * NO GLOBALS AND NO BARE TIMERS. Everything is reached through an injected `scope` - the window
 * in the browser, a plain object in the tests. That is also what keeps the module inside the
 * ESLint globals allowlist for `assets/purrz-*.js`, which grants `window`, `document` and
 * `navigator` but neither `setTimeout` nor `requestIdleCallback`.
 *
 * Unit tests: `tests/privacy/zendesk-defer.test.ts`. PURRZ (own, spec R4): unlike WOOPF's copy
 * this module pushes nothing to `dataLayer`; the analytics decisions are a later spec.
 */

/** The id Zendesk's own snippet looks for, and what makes injection idempotent. */
export const ZENDESK_SCRIPT_ID = 'ze-snippet';

/** Where Liquid keeps the snippet URL, so the widget key never moves into JavaScript. */
export const ZENDESK_SOURCE_ATTRIBUTE = 'data-pz-zendesk-src';

/** Any affordance that offers help: clicking one loads the widget immediately. */
export const HELP_SELECTOR = '[data-pz-help]';

/** Events that count as "this visitor is here and doing something". */
export const WAKE_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'scroll'];

/** How long after `load` the idle fallback waits before giving up and loading anyway. */
export const IDLE_TIMEOUT_MS = 5000;

/**
 * Read the snippet URL off the carrier tag.
 *
 * @param {Document} doc
 * @returns {string} the snippet URL, or '' when the tag is missing
 */
export const readSource = (doc) => {
  if (!doc || typeof doc.querySelector !== 'function') return '';
  const tag = doc.querySelector(`script[${ZENDESK_SOURCE_ATTRIBUTE}]`);
  if (!tag || typeof tag.getAttribute !== 'function') return '';
  return tag.getAttribute(ZENDESK_SOURCE_ATTRIBUTE) || '';
};

/**
 * Inject the widget. Idempotent by id, which is also the id Zendesk itself looks for, so a
 * second call - or a Theme Editor re-render - can never produce two widgets.
 *
 * @param {Document} doc
 * @param {string} source
 * @returns {boolean} true when this call injected the script
 */
export const loadZendesk = (doc, source) => {
  if (!doc || !source || typeof doc.createElement !== 'function') return false;
  if (typeof doc.getElementById === 'function' && doc.getElementById(ZENDESK_SCRIPT_ID)) {
    return false;
  }
  const script = doc.createElement('script');
  script.id = ZENDESK_SCRIPT_ID;
  script.src = source;
  script.async = true;
  if (doc.body && typeof doc.body.appendChild === 'function') doc.body.appendChild(script);
  return true;
};

/**
 * Wire every trigger.
 *
 * @param {Window} scope
 * @returns {() => void} a trigger that loads the widget now. The listeners call it themselves;
 *   it is returned so a caller that needs the widget before a visitor has touched anything can
 *   force the load, and so the tests can prove one call is enough. It is safe to call at any
 *   time and safe to call twice.
 */
export const initZendeskDefer = (scope) => {
  const doc = scope && scope.document;
  if (!doc) return () => {};

  const source = readSource(doc);
  if (!source) return () => {};

  let done = false;
  const teardowns = [];

  const fire = () => {
    if (done) return;
    done = true;
    for (const off of teardowns) off();
    teardowns.length = 0;
    loadZendesk(doc, source);
  };

  const on = (target, type, handler, options) => {
    if (!target || typeof target.addEventListener !== 'function') return;
    target.addEventListener(type, handler, options);
    teardowns.push(() => {
      if (typeof target.removeEventListener === 'function') {
        target.removeEventListener(type, handler, options);
      }
    });
  };

  for (const type of WAKE_EVENTS) {
    on(doc, type, fire, { once: true, passive: true, capture: true });
  }

  on(
    doc,
    'click',
    (event) => {
      const target = event && event.target;
      const matched =
        target && typeof target.closest === 'function' ? target.closest(HELP_SELECTOR) : null;
      if (matched) fire();
    },
    { capture: true },
  );

  const idle = () => {
    if (typeof scope.requestIdleCallback === 'function') {
      scope.requestIdleCallback(fire, { timeout: IDLE_TIMEOUT_MS });
    } else if (typeof scope.setTimeout === 'function') {
      scope.setTimeout(fire, IDLE_TIMEOUT_MS);
    }
  };

  if (doc.readyState === 'complete') idle();
  else on(scope, 'load', idle, { once: true });

  return fire;
};

/*
 * The auto-start. `typeof document` guards the Vitest run, which has no DOM at all.
 */
if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  initZendeskDefer(window);
}
